/*
 * Complete Your Routine
 *
 * Renders as <routine-builder>. Keeps the Add to cart button's total in step
 * with the ticked routine steps, and on submit adds the product plus every
 * ticked step to the cart in a single /cart/add.js request.
 *
 * Falls back to a plain cart redirect when the theme's HELPER_UTIL / drawer
 * are unavailable, so the module still works if theme internals change.
 */

(function () {
  'use strict';

  var CART_FORM_SELECTOR = 'form[action*="/cart/add"]';
  var SCOPE_SELECTOR = '.main-product__details, .main-wrapper-mobile-block, .main-product';
  var THEME_PRICE_SELECTOR = '.product-price, .subscription-price';

  function formatMoney(cents, format) {
    var value = Number(cents || 0);
    var pattern = format || '${{amount}}';

    function withDecimals(precision, thousands, decimal) {
      var fixed = (value / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/, function (_match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return withDecimals(0, ',', '.');
        case 'amount_with_comma_separator':
          return withDecimals(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDecimals(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDecimals(2, "'", '.');
        default:
          return withDecimals(2, ',', '.');
      }
    });
  }

  function themeHelper() {
    return typeof window.HELPER_UTIL !== 'undefined' ? window.HELPER_UTIL : null;
  }

  function themeEvents() {
    return typeof window._EVENT_HELPER !== 'undefined' ? window._EVENT_HELPER : null;
  }

  function showError(message) {
    var helper = themeHelper();
    var events = themeEvents();
    try {
      if (helper && events && typeof helper.dispatchCustomEvent === 'function') {
        helper.dispatchCustomEvent(events.triggerToastMessage, document, {
          toastType: 'common-errors',
          messageType: 'error',
          message: message
        });
        return;
      }
    } catch (error) {
      /* fall through */
    }
    window.alert(message);
  }

  function buttonState(button) {
    var helper = themeHelper();
    try {
      if (button && helper && typeof helper.submitButtonState === 'function') {
        return helper.submitButtonState(button);
      }
    } catch (error) {
      /* fall through */
    }
    return null;
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[character];
    });
  }

  class RoutineBuilder extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.showSavings = this.dataset.showSavings === 'true';

      this.addEventListener('change', this.onChange.bind(this));
      this.render();
      this.watchThemePrices();
    }

    /* The buy box this module belongs to. */
    scope() {
      return this.closest(SCOPE_SELECTOR) || document;
    }

    form() {
      return this.scope().querySelector(CART_FORM_SELECTOR) || document.querySelector(CART_FORM_SELECTOR);
    }

    submitButton() {
      var form = this.form();
      return form ? form.querySelector('[name="add"]') : null;
    }

    onChange(event) {
      var target = event.target;

      if (target.matches('[data-routine-variant]')) {
        var row = target.closest('[data-routine-row]');
        var option = target.options[target.selectedIndex];
        row.dataset.variant = option.value;
        row.dataset.price = option.dataset.price || row.dataset.price;
        var price = row.querySelector('[data-routine-price]');
        if (price) price.textContent = formatMoney(row.dataset.price, this.moneyFormat);
      }

      this.render();
    }

    /* Rows the customer has ticked, in page order. */
    selectedRows() {
      return Array.prototype.filter.call(
        this.querySelectorAll('[data-routine-row][data-addon]'),
        function (row) {
          var check = row.querySelector('[data-routine-check]');
          return check && check.checked && !check.disabled;
        }
      );
    }

    /* Steps still on offer, used to name one in the prompt under the button. */
    unselectedRows() {
      return Array.prototype.filter.call(
        this.querySelectorAll('[data-routine-row][data-addon]'),
        function (row) {
          var check = row.querySelector('[data-routine-check]');
          return check && !check.checked && !check.disabled;
        }
      );
    }

    fullPrice() {
      return this.selectedRows().reduce(function (sum, row) {
        return sum + Number(row.dataset.price || 0);
      }, Number(this.dataset.heroPrice || 0));
    }

    /* 0 unless the block is set to show savings. Shopify's automatic routine
       discount takes the same percentage off in the cart, so this figure is
       what the customer is charged. */
    discountRate() {
      if (!this.showSavings) return 0;
      var steps = this.selectedRows().length + 1;
      if (steps >= 3) return Number(this.dataset.discountThree || 0) / 100;
      if (steps === 2) return Number(this.dataset.discountTwo || 0) / 100;
      return 0;
    }

    total() {
      var full = this.fullPrice();
      return Math.round(full * (1 - this.discountRate()));
    }

    render() {
      this.renderRowStates();
      this.renderCta();
      this.renderNote();
    }

    /* A ticked step is in the cart, so it carries the same fill as this page's
       own product. */
    renderRowStates() {
      Array.prototype.forEach.call(
        this.querySelectorAll('[data-routine-row][data-addon]'),
        function (row) {
          var check = row.querySelector('[data-routine-check]');
          row.classList.toggle('is-selected', !!(check && check.checked && !check.disabled));
        }
      );
    }

    /* The theme prints its own price inside the button and toggles it between
       the one-time and subscribe figures. The routine total replaces both, so
       hide them rather than show two prices on one button. */
    hideThemePrices() {
      var button = this.submitButton();
      if (!button) return;

      Array.prototype.forEach.call(button.querySelectorAll(THEME_PRICE_SELECTOR), function (span) {
        if (span.style.display !== 'none') {
          span.style.setProperty('display', 'none', 'important');
        }
      });
    }

    /* The theme re-shows those spans on every purchase-option change, so keep
       them hidden as it does. */
    watchThemePrices() {
      this.hideThemePrices();

      var button = this.submitButton();
      if (!button || typeof window.MutationObserver === 'undefined') return;

      var self = this;
      this.priceObserver = new MutationObserver(function () {
        self.hideThemePrices();
      });

      Array.prototype.forEach.call(button.querySelectorAll(THEME_PRICE_SELECTOR), function (span) {
        self.priceObserver.observe(span, { attributes: true, attributeFilter: ['style', 'class'] });
      });
    }

    /* The routine total sits beside the theme's Add to cart label, spaced by
       the stylesheet: ADD TO CART   $156.00 */
    renderCta() {
      var button = this.submitButton();
      if (!button) return;

      this.hideThemePrices();

      var label = button.querySelector('.btn--text') || button;
      var totalNode = label.querySelector('[data-routine-cta-total]');

      if (!totalNode) {
        totalNode = document.createElement('span');
        totalNode.className = 'routine-cta-total';
        totalNode.setAttribute('data-routine-cta-total', '');
        var firstSpan = label.querySelector('span');
        if (firstSpan && firstSpan.nextSibling) {
          label.insertBefore(totalNode, firstSpan.nextSibling);
        } else {
          label.appendChild(totalNode);
        }
      }

      totalNode.textContent = formatMoney(this.total(), this.moneyFormat);
    }

    /* Optional line under the buy buttons. Nothing is rendered when the block
       has no prompt and no savings to report. */
    renderNote() {
      var form = this.form();
      if (!form) return;

      var buttons = form.querySelector('.product-form__buttons');
      if (!buttons) return;

      var note = buttons.parentNode.querySelector('[data-routine-note]');
      var rate = this.discountRate();
      var html = '';

      if (rate > 0) {
        var full = this.fullPrice();
        var total = this.total();
        var text = (this.dataset.noteSavings || '')
          .replace('[full]', formatMoney(full, this.moneyFormat))
          .replace('[total]', formatMoney(total, this.moneyFormat))
          .replace('[saved]', formatMoney(full - total, this.moneyFormat));

        html = '<span class="routine-note__badge">Save ' + Math.round(rate * 100) + '%</span>' + escapeHtml(text);
      } else {
        var next = this.unselectedRows()[0];
        var nextName = next ? String(next.dataset.title || '').split(' ')[0] : '';
        var prompt = (this.dataset.noteEmpty || '').replace('[product]', nextName).trim();
        html = prompt ? escapeHtml(prompt) : '';
      }

      if (!html) {
        if (note) note.remove();
        return;
      }

      if (!note) {
        note = document.createElement('p');
        note.className = 'routine-note';
        note.setAttribute('data-routine-note', '');
        buttons.parentNode.insertBefore(note, buttons.nextSibling);
      }

      note.innerHTML = html;
    }

    /* Line items for /cart/add.js: the product being viewed, then each ticked step. */
    items(form) {
      var items = [];
      var properties = {};

      new FormData(form).forEach(function (value, key) {
        var match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      });

      var idField = form.querySelector('[name="id"]');
      var quantityField = form.querySelector('[name="quantity"]');
      var sellingPlanField = form.querySelector('[name="selling_plan"]');

      var hero = {
        id: Number((idField && idField.value) || this.dataset.heroVariant),
        quantity: Number((quantityField && quantityField.value) || 1)
      };
      if (Object.keys(properties).length) hero.properties = properties;
      if (sellingPlanField && sellingPlanField.value) hero.selling_plan = Number(sellingPlanField.value);
      items.push(hero);

      var source = this.dataset.heroHandle;
      this.selectedRows().forEach(function (row) {
        items.push({
          id: Number(row.dataset.variant),
          quantity: 1,
          properties: { _routine: source }
        });
      });

      return items;
    }
  }

  customElements.define('routine-builder', RoutineBuilder);

  /*
   * When the module renders from the buy-button snippet it lands inside the
   * button's column, a flex item sharing a centred row with the quantity
   * selector. Left there it squeezes the module and pushes the quantity
   * selector out of line, so move it above the whole row. Mounted as its own
   * block there is no such row and this does nothing.
   */
  function placeBuilders() {
    var builders = document.querySelectorAll('routine-builder');

    Array.prototype.forEach.call(builders, function (builder) {
      var row = builder.closest('.main-quantity-buy');
      if (!row || !row.parentNode) return;
      if (row.offsetParent === null) return;

      row.parentNode.insertBefore(builder, row);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', placeBuilders);
  } else {
    placeBuilders();
  }

  /* The module renders once per layout; only one of them is on screen. */
  function visibleBuilder(form) {
    var builders = document.querySelectorAll('routine-builder');
    for (var i = 0; i < builders.length; i++) {
      if (builders[i].offsetParent === null) continue;
      if (!builders[i].selectedRows().length) continue;
      if (form && builders[i].form() !== form) continue;
      return builders[i];
    }
    return null;
  }

  /*
   * Open the pull-out cart through the one opener the theme has, in
   * routine-cart-open.js. It watches every add to the cart, refreshes the
   * drawer and sees it open, so this module does not need its own version of
   * that and must not have one: two of them racing is what shut the drawer as
   * often as it opened it.
   */
  function showCart(submitter) {
    window.currentActiveElement = submitter;

    if (typeof window.routineShowCart === 'function') {
      window.routineShowCart();
      return true;
    }

    /* Without it, at least ask the theme to redraw the cart. */
    var helper = themeHelper();
    var events = themeEvents();

    if (!helper || !events || !document.querySelector('drawer-cart')) return false;
    if (events.updateAndShowCart) helper.dispatchCustomEvent(events.updateAndShowCart, document);

    return true;
  }

  function addRoutine(builder, form, submitter) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var state = buttonState(submitter);

    if (state) state.loadingState();

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items: builder.items(form) })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(function () {
        if (state) state.completeState();

        /* Never navigate. The customer stays on the product page with the cart
           over it, free to carry on shopping. Without a drawer there is simply
           nothing to open, and the header count still moves. */
        showCart(submitter);
      })
      .catch(function (error) {
        showError((error && error.description) || 'Something went wrong adding your routine. Please try again.');
      })
      .finally(function () {
        if (state) {
          setTimeout(function () {
            state.resetState(1000);
            state.disableState(false);
          }, 1000);
        }
      });
  }

  /*
   * Capture phase on the window, which is the first node the event reaches.
   * Registering here rather than on the document puts this ahead of every
   * other submit listener on the page, theme or app, so stopping propagation
   * really does stop all of them: the single-item add is replaced by the
   * multi-item one and nothing else gets to act on the same submit.
   */
  window.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches(CART_FORM_SELECTOR)) return;

      var builder = visibleBuilder(form) || visibleBuilder(null);
      if (!builder) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      addRoutine(builder, form, event.submitter || form.querySelector('[name="add"]'));
    },
    true
  );

  /* Theme editor re-renders the section on every settings change. */
  document.addEventListener('shopify:section:load', function () {
    placeBuilders();
    document.querySelectorAll('routine-builder').forEach(function (builder) {
      if (typeof builder.render === 'function') builder.render();
      if (typeof builder.watchThemePrices === 'function') builder.watchThemePrices();
    });
  });
})();
